import type { Coordinates } from '$lib/components/map/types';
import type { PoiFilterId, PoiFilterValue } from '$lib/components/pois/types';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type { PoisStoreContent } from '$routes/(map)/types';
import type { PageLoadEvent as ZoiPageLoadEvent } from './$types';
import type { PageLoadEvent as ZoiFilterPageLoadEvent } from './filters/$types';
import type { PageLoadEvent as ZoiDatasheetPageLoadEvent } from './poi/$types';
import { load_GetPoisWithinRadius } from '$houdini';
import { defaultMapBoundsByCountry } from '$lib/components/map/constants';
import { defaultPoiCategories } from '$lib/constants/searchParams';
import { getCountryFromLocale } from '@viamichelin/business';
import { derived, type Readable } from 'svelte/store';
import { paramsHasCategory } from '../../../../../params/poiCategory';

const POIS_PER_PAGE = 50;

type PageLoadEvent = ZoiPageLoadEvent | ZoiFilterPageLoadEvent | ZoiDatasheetPageLoadEvent;

interface GetPoiStoreArgs {
	center: Coordinates;
	radius: number;
	event: PageLoadEvent;
	page: number;
	searchParams: SearchParams;
}

export async function getPoisWithinRadiusStore({
	center,
	radius,
	event,
	page,
	searchParams,
}: GetPoiStoreArgs): Promise<Readable<Omit<PoisStoreContent, 'selectedPoiData'>>> {
	const { poiCategories, filters, bounds } = searchParams;
	const { locale } = await event.parent();

	const poiCategory = paramsHasCategory(event.params) ? event.params.category : undefined;
	const categories
		= poiCategories ?? (poiCategory ? [poiCategory] : undefined) ?? defaultPoiCategories;

	const { GetPoisWithinRadius } = await load_GetPoisWithinRadius({
		blocking: true,
		event,
		variables: {
			input: {
				perPage: POIS_PER_PAGE,
				page,
				types: categories,
				poiFilters: (Object.entries(filters ?? {}) as [PoiFilterId, PoiFilterValue][]).map(
					([key, value]) => ({
						id: key,
						value,
					}),
				),
				center,
				radius,
			},
		},
	});

	return derived(GetPoisWithinRadius, ($store) => {
		if (!$store.data) {
			// should never happen because blocking set to true
			throw new Error('getPOIsWithinRadius data is undefined');
		}

		const { items, totalCount, pageCount, availabilityStatus }
			= $store.data.getPoisWithinRadius.poiList;

		return {
			availabilityStatus: availabilityStatus ?? undefined,
			fetching: $store.fetching,
			poisList: items ?? [],
			poisCount: totalCount,
			pageCount,
			mapBounds: bounds ?? defaultMapBoundsByCountry[getCountryFromLocale(locale)],
			poiCategories: categories,
			hasError: !!$store.errors,
		};
	});
}
